import { createHash } from "node:crypto";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { resolveRepoRoot } from "./brain-paths.js";

// Architect drift detection via a render-time sidecar manifest + source content hash.
//
// ── canonical hash (MUST stay byte-for-byte identical to the writer in
// scripts/write-skill-sidecar.mjs — both hash the entire source file):
//
//   createHash("sha256").update(readFileSync(sourcePath)).digest("hex")

/**
 * Read the `version:` frontmatter field from a markdown file.
 * Returns "" when the file is missing or has no version field.
 */
function readVersionFrontmatter(file: string): string {
  if (!fs.existsSync(file)) return "";
  const text = fs.readFileSync(file, "utf8");
  // Frontmatter is delimited by a leading `---` line and a closing `---` line.
  if (!text.startsWith("---")) return "";
  const closeIdx = text.indexOf("\n---", 3);
  if (closeIdx < 0) return "";
  const fm = text.slice(3, closeIdx + 1);
  const m = /^version:\s*["']?([^"'\n]+)["']?\s*$/m.exec(fm);
  return m ? m[1].trim() : "";
}

/**
 * Compute SHA-256 hex digest of a file's raw bytes.
 * Canonical algorithm — must stay in sync with scripts/write-skill-sidecar.mjs.
 */
function sha256File(filePath: string): string {
  return createHash("sha256").update(fs.readFileSync(filePath)).digest("hex");
}

// ── sidecar location resolution ────────────────────────────────────────────
//
// A rendered m8t-architect can live in more than one host surface (see
// targets/local/README.md and guides/install/m8t.md). Each host writes its
// sidecar to a different directory shape:
//   - Claude Code / GitHub Copilot CLI: one directory per persona.
//   - VS Code Copilot Chat: a single flat prompts directory shared by every
//     rendered persona — write-skill-sidecar.mjs disambiguates with a
//     per-persona filename there (and everywhere else, for one consistent rule).

/** Directory + human-readable label for one host's rendered-skill location. */
interface SidecarLocation {
  host: string;
  dir: string;
}

/** Current sidecar filename, scoped per persona (see scripts/write-skill-sidecar.mjs). */
const SIDECAR_FILENAME = "m8t-architect.m8t-skill.json";

// Pre-per-persona-filename renders (any host, but in practice only ever
// Claude Code, whose per-persona directory made the fixed name unambiguous)
// wrote this fixed name instead. Checked as a fallback so an install rendered
// before this fix doesn't need a re-render just to pass the check.
const LEGACY_SIDECAR_FILENAME = ".m8t-skill.json";

/**
 * Resolve VS Code's per-OS "User" data directory, per targets/local/README.md /
 * guides/install/m8t.md ("VS Code Copilot Chat" § step 0): macOS
 * `~/Library/Application Support/Code/User`, Windows `%APPDATA%\Code\User`.
 * Returns null on hosts the install guides don't document a VS Code path for
 * (e.g. Linux) — detection is skipped there rather than guessed at.
 */
function vsCodeUserDataDir(): string | null {
  if (process.platform === "darwin") {
    return path.join(os.homedir(), "Library", "Application Support", "Code", "User");
  }
  if (process.platform === "win32") {
    const appData = process.env.APPDATA ?? path.join(os.homedir(), "AppData", "Roaming");
    return path.join(appData, "Code", "User");
  }
  return null;
}

/**
 * Resolve VS Code's prompts subfolder, mirroring the runtime probe in
 * guides/install/m8t.md's "VS Code Copilot Chat" § step 0: prefer `prompts/`
 * if it exists, fall back to `prompt-files/` (older VS Code), else default to
 * `prompts/` (not created yet — nothing is installed either way).
 */
function vsCodePromptsDir(): string | null {
  const userData = vsCodeUserDataDir();
  if (!userData) return null;
  const prompts = path.join(userData, "prompts");
  if (fs.existsSync(prompts)) return prompts;
  const promptFiles = path.join(userData, "prompt-files");
  if (fs.existsSync(promptFiles)) return promptFiles;
  return prompts;
}

// Candidate GitHub Copilot CLI skills roots, per guides/install/m8t.md's
// runtime probe (§ "GitHub Copilot CLI" step 0) — first one that exists wins
// there; here we just check all three, in the same order.
const COPILOT_CLI_SKILLS_ROOTS = [
  [".copilot", "skills"],
  [".config", "github-copilot", "skills"],
  [".github", "copilot", "skills"],
];

/**
 * Every host directory m8t-architect might be rendered into, in probe order:
 * (a) Claude Code, (b) VS Code Copilot Chat, (c) GitHub Copilot CLI.
 */
function sidecarLocations(): SidecarLocation[] {
  const home = os.homedir();
  const locations: SidecarLocation[] = [
    { host: "Claude Code", dir: path.join(home, ".claude", "skills", "m8t-architect") },
  ];

  const vsCodeDir = vsCodePromptsDir();
  if (vsCodeDir) {
    locations.push({ host: "VS Code Copilot Chat", dir: vsCodeDir });
  }

  for (const segments of COPILOT_CLI_SKILLS_ROOTS) {
    locations.push({
      host: "GitHub Copilot CLI",
      dir: path.join(home, ...segments, "m8t-architect"),
    });
  }

  return locations;
}

/**
 * Probe every host location (current filename, then the legacy fallback) and
 * return the sidecar to trust. First one found wins; if several hosts have a
 * sidecar, the freshest by mtime wins (most likely the install the user is
 * actually running today).
 */
function resolveSidecar(): { path: string; host: string } | null {
  let best: { path: string; host: string; mtimeMs: number } | null = null;
  for (const loc of sidecarLocations()) {
    for (const filename of [SIDECAR_FILENAME, LEGACY_SIDECAR_FILENAME]) {
      const candidate = path.join(loc.dir, filename);
      let mtimeMs: number;
      try {
        mtimeMs = fs.statSync(candidate).mtimeMs;
      } catch {
        continue; // not present at this candidate
      }
      if (!best || mtimeMs > best.mtimeMs) {
        best = { path: candidate, host: loc.host, mtimeMs };
      }
    }
  }
  return best;
}

export interface ArchitectDrift {
  match: boolean;
  sourceVersion: string;       // from source frontmatter (display)
  installedVersion: string;    // from sidecar (display); "" when no/unreadable sidecar
  advice?: string;             // human-readable next step
}

export function checkArchitectDrift(): ArchitectDrift {
  // 1. Resolve paths
  const sourcePath = path.join(
    resolveRepoRoot(),
    "personas",
    "m8t-architect",
    "persona.md",
  );

  // 2. Source missing
  if (!fs.existsSync(sourcePath)) {
    return {
      match: false,
      sourceVersion: "",
      installedVersion: "",
      advice: `Source persona not found at ${sourcePath}. Check ~/.m8t/repo-root points at a valid checkout.`,
    };
  }

  // 3. Read source version (for display)
  const sourceVersion = readVersionFrontmatter(sourcePath);

  // 4. Sidecar missing or unreadable/malformed — probe every host location
  const resolved = resolveSidecar();
  let sidecar: { version?: string; sourceSha256?: string } | null = null;
  if (resolved) {
    try {
      const raw: unknown = JSON.parse(fs.readFileSync(resolved.path, "utf8"));
      if (raw !== null && typeof raw === "object") {
        const r = raw as Record<string, unknown>;
        sidecar = {
          version: typeof r.version === "string" ? r.version : undefined,
          sourceSha256: typeof r.sourceSha256 === "string" ? r.sourceSha256 : undefined,
        };
      }
    } catch {
      sidecar = null; // malformed JSON → treat as missing
    }
  }

  if (!sidecar || typeof sidecar.sourceSha256 !== "string") {
    const checked = sidecarLocations().map((l) => `${l.host} (${l.dir})`).join("; ");
    return {
      match: false,
      sourceVersion,
      installedVersion: "",
      advice: `Architect skill not installed (or installed before sidecar support). Checked: ${checked}. Re-run the install guide for your host from guides/install/m8t.md to render it.`,
    };
  }

  // 5. Compare hashes
  const installedVersion = typeof sidecar.version === "string" ? sidecar.version : "";
  const currentHash = sha256File(sourcePath);

  if (currentHash === sidecar.sourceSha256) {
    return { match: true, sourceVersion, installedVersion };
  }

  const sameVersion = !!installedVersion && installedVersion === sourceVersion;
  return {
    match: false,
    sourceVersion,
    installedVersion,
    advice: sameVersion
      ? `m8t-architect content drift (v${sourceVersion} unchanged, body edited since render). Re-render via the install guide for your host (guides/install/m8t.md).`
      : `Installed m8t-architect${installedVersion ? ` (v${installedVersion})` : ""} is out of date with source${sourceVersion ? ` (v${sourceVersion})` : ""}. Re-render via the install guide for your host (guides/install/m8t.md).`,
  };
}
